import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import { showError } from '@app/utils/message';
import { ProductGroupModel } from '@app/interfaces/product/group';
import { createProductGroupApi, deleteProductGroupApi, getProductGroupsApi, updateProductGroupApi } from '@app/api/productGroups.api';

export interface ProductGroupState {
  data: ProductGroupModel[];
  loading: boolean;
  error: string | null;
}

const initialState: ProductGroupState = {
  data: [],
  loading: false,
  error: null,
};

interface ResponseData {
  data: ProductGroupModel;
  message: string;
}

// Thunks
export const getProductGroups = createAsyncThunk<ProductGroupModel[]>(
  'product_group/getProductGroups',
  async (_, { rejectWithValue }) => {
    try {
      const stores = await getProductGroupsApi()
      return stores || [];

    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const createProductGroup = createAsyncThunk<ResponseData, ProductGroupModel>(
  'product_group/createProductGroup', async (data) => {
  const response = await createProductGroupApi(data);
  return response;
});

export const updateProductGroup = createAsyncThunk<ResponseData, { productGoupId: number; formData: ProductGroupModel }>(
  'product_group/updateProductGroup',
  async ({ productGoupId, formData }) => {
    const response = await updateProductGroupApi(productGoupId, formData);
    return response;
  },
);

export const deleteProductGroup = createAsyncThunk<{ productGoupId: number; response: any }, number>(
  'product_group/deleteProductGroup',
  async (productGoupId, { rejectWithValue }) => {
    try {
      const response = await deleteProductGroupApi(productGoupId);
      return { productGoupId, response };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

// Slice
export const productGroupSlice = createSlice({
  name: 'product_group',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProductGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProductGroups.fulfilled, (state, action) => {
        state.data = action.payload
        state.loading = false;
      })
      .addCase(createProductGroup.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.data.unshift(data);
        message.success(newMessage);
      })
      .addCase(updateProductGroup.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        const dataId = typeof data.id === 'string' ? parseInt(data.id, 10) : data.id;

        const updatedIndex = state.data.findIndex((item) => item.id === dataId);
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data;
          message.success(newMessage);
        }
      })
      .addCase(deleteProductGroup.fulfilled, (state, action) => {
        const { productGoupId, response } = action.payload;

        state.data = state.data.filter((supplier) => supplier.id !== productGoupId);
        message.success(response.message);
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        showError(action.payload?.message || action.payload?.error || action?.error?.message);
      });
    }
  }
);

export default productGroupSlice.reducer;